import { useEffect } from 'react';
import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  useUnknownConfigProperty,
} from '@leagueplatform/core';
import { useIntl } from '@leagueplatform/locales';
import { captureMessage } from '@leagueplatform/observability';
import type { UserMenuSection } from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { INTERNAL_LINKS } from 'common/links';
import { useIsCaregiverShell } from 'hooks/use-is-caregiver.hook';
import { useSelectedMarket } from 'hooks/use-selected-market.hook';
import { openDemoPicker } from 'common/demo-mode';

// Same key the route guard reads — see require-market-and-persona.component.
const PERSONA_SELECTOR_FLAG_KEY = 'release_eos_persona_selector_enabled';

export const useUserMenuConfig = (): { userMenu: UserMenuSection[] } => {
  const { formatMessage } = useIntl();
  // The Account section is subscriber-specific (member routes). A
  // non-impersonating caregiver would only bounce back to caregiver home, so hide
  // it for the caregiver shell (and while the claim resolves); About + Log Out
  // stay available.
  const isCaregiverShell = useIsCaregiverShell();

  // The persona selector is a sales-demo surface, so the entry point is gated
  // on the same env-level flag as the setup route. Flag off leaves the menu
  // exactly as it was.
  const isPersonaSelectorEnabled = Boolean(
    useUnknownConfigProperty(PERSONA_SELECTOR_FLAG_KEY, false),
  );
  const {
    label: marketLabel,
    resolved: isMarketResolved,
    settled: isMarketSettled,
  } = useSelectedMarket();

  useEffect(() => {
    // One event per boundary crossing, not per render: `isMarketResolved` starts
    // false while the profile is in flight, and logging then would report a
    // resolution failure that never happened.
    if (!isMarketSettled) return;

    captureMessage('eos_switch_experience_item evaluation', {
      severityLevel: 'info',
      context: {
        menuItem: {
          component: 'eos_switch_experience_item',
          flag_enabled: isPersonaSelectorEnabled,
          market_resolved: isMarketResolved,
          outcome: isPersonaSelectorEnabled ? 'shown' : 'hidden',
        },
      },
    });
  }, [isPersonaSelectorEnabled, isMarketResolved, isMarketSettled]);

  const userMenu: UserMenuSection[] = [
    ...(isCaregiverShell !== false
      ? []
      : [
          {
            heading: formatMessage({ id: 'STR_ACCOUNT' }),
            items: [
              {
                label: formatMessage({ id: 'STR_PERSONAL_INFO' }),
                to: INTERNAL_LINKS.PERSONAL_INFO,
              },
              {
                label: formatMessage({ id: 'STR_MY_CAREGIVERS' }),
                to: INTERNAL_LINKS.MY_CAREGIVERS,
              },
              {
                label: formatMessage({ id: 'STR_COMMUNICATION' }),
                to: INTERNAL_LINKS.COMMUNICATION,
              },
              {
                label: formatMessage({ id: 'STR_HEALTH_HISTORY' }),
                to: getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory),
              },
              {
                label: formatMessage({ id: 'STR_ABOUT_ME' }),
                to: getModulePath(LEAGUE_MODULE_NAMES.aboutMe),
              },
            ],
          },
        ]),
    {
      heading: formatMessage({ id: 'STR_ABOUT' }),
      items: [
        {
          label: formatMessage({ id: 'STR_DEMO_START' }),
          onClick: openDemoPicker,
        },
        // Sits next to "Start a demo" rather than under its own `Experience`
        // heading: regrouping would move Pod 8's Sales Mode item (CETF-699),
        // which is a follow-on. The badge is omitted rather than left blank
        // when no market resolves, so the row degrades to a plain entry.
        ...(isPersonaSelectorEnabled
          ? [
              {
                label: formatMessage({ id: 'STR_SWITCH_EXPERIENCE' }),
                to: APP_MANAGED_ROUTES.SETUP_MARKET_EXPERIENCE,
                ...(marketLabel ? { badge: marketLabel } : {}),
              },
            ]
          : []),
        {
          label: formatMessage({ id: 'STR_ABOUT_EOS_HEALTH' }),
          to: INTERNAL_LINKS.ABOUT,
        },
        {
          label: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
          to: INTERNAL_LINKS.POLICY,
        },
        {
          label: formatMessage({ id: 'STR_TERMS_OF_SERVICE' }),
          to: INTERNAL_LINKS.TERMS,
        },
        {
          label: formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' }),
          to: INTERNAL_LINKS.DELETE_ACCOUNT,
        },
      ],
    },
    {
      items: [
        {
          label: formatMessage({ id: 'STR_LOG_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          variant: 'critical',
        },
      ],
    },
  ];

  return { userMenu };
};
